import { Component, Input, EventEmitter, Output, OnChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { Channel } from 'src/app/channel/model/channel';
import { ChannelService } from 'src/app/core/service/channel.service';
import { FlowableService } from 'src/app/core/service/flowable.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';

@Component({
  selector: 'app-channel-status',
  templateUrl: './channel-status.component.html',
  styleUrls: ['./channel-status.component.scss']
})
export class ChannelStatusComponent implements OnChanges
{
  @Input() public channel: Channel;
  @Input() public dialogOpened: boolean;
  @Output() public closing = new EventEmitter();
  @Output() public save = new EventEmitter();

  public channelStatus = { text: null, value: null };

  public channelStatuses: Array<{ text: string; value: string }>;

  public editForm: any;

  public bpmn: any;

  private deployedBpmn: any = null;

  /**
   * Current search term entered from a comboxbox
   */

  constructor(private channelService: ChannelService,
    private messageHandlerService: MessageHandlerService,
    private flowableService: FlowableService,
    private fb: UntypedFormBuilder) { }

  public get status(): UntypedFormControl
  {
    return this.editForm.get('status');
  }

  public ngOnChanges(): void
  {
    if (this.channel !== undefined && this.channel !== null)
    {
      if (this.channel.status === 'Archived')
      {
        this.channelStatuses = this.channelService.channelStatusesArchived;
      }
      else
      {
        this.channelStatuses = this.channelService.channelStatuses;
      }

      this.channelStatus.value = this.channel.status;
      this.channelStatus.text = this.channelStatuses.find(s => s.value === this.channelStatus.value).text;
      this.initModel(this.channelStatus);
    }
  }

  public updateStatus(): void
  {
    this.channelService.getChannel(this.channel.id).subscribe({
      next: (channel) =>
      {
        const disallowOnline = channel.status === 'Online' || channel.status === 'Archived' ||
          channel.active === false || channel.id === null || channel.bpmKey === undefined || channel.bpmKey === null;

        const disallowSimulated = channel.bpmId === null || channel.status === 'Deleted' || channel.status === 'Archived' ||
          channel.active === false;

        const disallowArchived = channel.status === 'Offline' || channel.status === 'Archived' ||
          channel.id === null || channel.active === false || channel.bpmKey === undefined || channel.bpmKey === null;

        const disallowOffline = channel.status === 'Offline' || channel.status === 'Archived' ||
          channel.active === false || channel.bpmKey === undefined || this.channel.bpmKey === null;

        // Validate if update can be performed
        if ((this.status.value === 'Online' && disallowOnline) || (this.status.value === 'Simulated' && disallowSimulated))
        {
          this.messageHandlerService.displayError('CANNOT-CHANGE-CHANNEL-STATUS-NO-WORKFLOW');
        }
        else if ((this.status.value === 'Archived' && disallowArchived) || (this.status.value === 'Offline' && disallowOffline))
        {
          this.messageHandlerService.displayError('CANNOT-CHANGE-CHANNEL-STATUS');
        }
        else
        {
          this.updateChannelStatus();
        }
      }
    });
  }

  public closeDialog(): void
  {
    this.closing.emit();
    this.channelStatus = { text: null, value: null };
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.disabled !== undefined ? itemArgs.dataItem.disabled : false;
  }

  /**
   * Include the bpmn in the flowable app and return the app object.
   *
   * @param flowableApp Flowable app
   * @param bpmn BPMN object
   */
  public includeBpm(flowableApp, bpmn): any
  {
    return {
      appDefinition:
      {
        id: flowableApp.id,
        name: flowableApp.name,
        key: flowableApp.key,
        description: flowableApp.description,
        version: flowableApp.version,
        definition: // this is the BPM
        {
          models: [
            bpmn // we only support a single process for each channel
          ],
          theme: 'theme-1', // default theme color
          icon: 'glyphicon-asterisk' // default icon
        }
      },
      publish: true // must have this property to publish the app.
    };
  }

  private initModel(channelStatus: { text: string; value: string })
  {
    if (channelStatus === undefined)
    {
      return;
    }
    // Set form
    this.editForm = this.fb.group({
      status: [this.channelStatus.value, Validators.required]
    });
  }

  private updateChannelStatus(): void
  {
    this.channelService.updateStatus(this.status.value, this.channel.id).subscribe({
      next: (data) =>
      {
        this.save.emit(data);
        this.channelStatus = { text: null, value: null };
        this.messageHandlerService.displaySuccess('CHANNEL-SAVED-SUCCESSFULLY');
        if (this.status.value === 'Online' || this.status.value === 'Simulated')
        {
          // retrieve the modeler's BPMN
          this.flowableService.getModel(data.appId).subscribe({
            next: (model) =>
            {
              this.flowableService.getModel(data.bpmId).subscribe({
                next: (bpmn) =>
                {
                  this.bpmn = bpmn;
                  const updatedApp = this.includeBpm(model, this.bpmn);
                  this.flowableService.updateApp(model.id, updatedApp).subscribe({
                    next: (appData) =>
                    {
                      if (appData.appDefinition !== null)
                      {
                        const key = appData.appDefinition.key;
                        const id = appData.appDefinition.id;
                        // step 3: get the deployed BPMN by the app's key.
                        this.flowableService.getDeployedBpmnByAppKey(key).subscribe({
                          next: (bpmnId) =>
                          {
                            this.deployedBpmn = bpmnId.data[0];
                            this.deployedBpmn.appId = id;
                            this.channelService.updateModel(this.channel.id, this.deployedBpmn).subscribe({
                              next: (channel) =>
                              {
                                this.channel = channel;
                              }
                            });
                          },
                          error: (error) =>
                          {
                            this.messageHandlerService.displayError(error);
                          }
                        });
                      }
                      else if (appData.error)
                      {
                        this.messageHandlerService.displayError('FAILED-TO-VALIDATE-PROCESS');
                      }
                    }
                  });
                }
              });
            }
          });
        }
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }
}
